import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormHelperText,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ScienceIcon from '@mui/icons-material/Science';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate } from 'react-router-dom';

import PageHeader from '../components/PageHeader';
import Button from '../components/common/ActionButton';
import ResourceLink from '../components/common/ResourceLink';
import { trackFrontendEvent } from '../api/events';
import {
  fetchFunctionalHistorySummary,
  generateSampleFunctionalHistory,
  importFunctionalHistory,
} from '../api/history';
import { createProject, Project } from '../api/projects';
import { api } from '../utils/apiClient';
import { formatApiError } from '../utils/formatApiError';
import { useAuth } from '../context/authContext';
import { useProject } from '../context/ProjectContext';
import { useSubscription } from '../context/SubscriptionContext';
import { DOC_PATHS, TEMPLATE_PATHS } from '../utils/onboardingResources';
import { normalizeUserFacingError } from '../utils/normalizeUserFacingError';
import {
  outlineActionButtonSx,
  primaryActionButtonSx,
  secondaryActionButtonSx,
} from '../utils/actionButtonStyles';
import {
  type StartPath,
} from '../features/onboarding/utils/onboardingDerivation';
import { useOnboardingStartPath } from '../features/onboarding/hooks/useOnboardingStartPath';
import { useOnboardingStatus } from '../features/onboarding/hooks/useOnboardingStatus';

type FunctionalLibraryListResponse = {
  data: Array<{
    id: number;
    test_id: string;
    is_sample_library?: boolean;
    library_source?: string;
    constraints?: Record<string, unknown> | null;
  }>;
  total: number;
};

type SampleLoadResponse = {
  added: number;
  skipped: number;
  history_runs_created?: number;
  history_results_inserted?: number;
  loaded?: boolean;
  already_loaded?: boolean;
  history_bootstrapped?: boolean;
  history_error?: string | null;
};

type HistoryGenerationResponse = {
  generated_count?: number;
  results_inserted?: number;
  rows_inserted?: number;
  total_available_history_count?: number;
};

type UploadResult = {
  added?: number;
  updated?: number;
  skipped?: number;
  errors?: Array<{ index: number; error: string }>;
};

const StepCard = ({
  stepId,
  title,
  description,
  complete,
  active = false,
  children,
}: {
  stepId?: string;
  title: string;
  description: string;
  complete: boolean;
  active?: boolean;
  children: React.ReactNode;
}) => (
  <Card
    id={stepId}
    className="enterprise-card"
    sx={{
      borderRadius: 3,
      borderColor: active
        ? 'rgba(57, 255, 20, 0.28)'
        : complete
          ? 'rgba(57, 255, 20, 0.2)'
          : 'rgba(255,255,255,0.08)',
      boxShadow: active
        ? '0 0 0 1px rgba(57,255,20,0.18)'
        : complete
          ? '0 12px 28px rgba(57, 255, 20, 0.08)'
          : 'none',
    }}
  >
    <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {description}
            </Typography>
          </Box>
          <Chip
            icon={complete ? <CheckCircleIcon /> : <LockOpenIcon />}
            label={complete ? 'Complete' : 'Pending'}
            size="small"
            sx={{
              color: complete ? '#DFFFE6' : '#C9D1D9',
              bgcolor: complete ? 'rgba(57, 255, 20, 0.12)' : 'rgba(255,255,255,0.06)',
              border: complete
                ? '1px solid rgba(57, 255, 20, 0.2)'
                : '1px solid rgba(255,255,255,0.08)',
              '& .MuiChip-icon': {
                color: complete ? '#39FF14' : '#9AA4B2',
              },
            }}
          />
        </Stack>
        {children}
      </Stack>
    </CardContent>
  </Card>
);

const firstNumber = (...values: unknown[]): number | null => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }
  return null;
};

export const buildSampleHistorySuccessMessage = (
  result: HistoryGenerationResponse,
  fallbackAvailableCount: number
) => {
  const generatedCount = firstNumber(
    result.generated_count,
    result.results_inserted,
    result.rows_inserted
  );
  const availableCount = firstNumber(
    result.total_available_history_count,
    generatedCount,
    fallbackAvailableCount
  ) || 0;

  if ((generatedCount || 0) > 0) {
    return {
      count: availableCount,
      message: `Sample execution history is ready. Generated ${generatedCount} historical result records for the sample project.`,
    };
  }

  if (availableCount > 0) {
    return {
      count: availableCount,
      message: `Sample execution history is ready. ${availableCount} historical result records are available for the sample project.`,
    };
  }

  return {
    count: 0,
    message: 'Sample execution history is ready. No historical result records were generated.',
  };
};

export default function OnboardingPage() {
  const { authData } = useAuth();
  const navigate = useNavigate();
  const {
    currentProject,
    availableProjects,
    setCurrentProject,
    refreshProjects,
  } = useProject();
  const { subscription, refreshSubscription } = useSubscription();

  const [projectName, setProjectName] = useState('');
  const [projectLoading, setProjectLoading] = useState(false);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [observabilityLoading, setObservabilityLoading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [projectError, setProjectError] = useState<string | null>(null);
  const [observabilityError, setObservabilityError] = useState<string | null>(null);
  const [projectSuccess, setProjectSuccess] = useState<string | null>(null);
  const [librarySuccess, setLibrarySuccess] = useState<string | null>(null);
  const [observabilitySuccess, setObservabilitySuccess] = useState<string | null>(null);
  const [sampleSetupError, setSampleSetupError] = useState<string | null>(null);
  const [samplePreloadPending, setSamplePreloadPending] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historySuccess, setHistorySuccess] = useState<string | null>(null);

  const activeProject = currentProject || (availableProjects[0] as Project | undefined) || null;
  const hasProject = !!activeProject;
  const accountReady = !!authData?.organizationId;

  useEffect(() => {
    if (!currentProject && availableProjects.length === 1) {
      setCurrentProject(availableProjects[0] as Project);
    }
  }, [availableProjects, currentProject, setCurrentProject]);

  const {
    startPath,
    selectedStartOption,
    setSelectedStartOption,
    startChoiceCommitted,
    setStartChoiceCommitted,
    startChoiceError,
    setStartChoiceError,
    showStartOptionPicker,
    setShowStartOptionPicker,
    pendingStartPathStorageKey,
    projectStartPathStorageKey,
    syncStartPathFromStorage,
    selectStartPath,
    commitStartPath,
    clearStoredStartPath,
  } = useOnboardingStartPath({
    organizationId: authData?.organizationId,
    activeProjectId: activeProject?.id,
    hasProject,
  });

  const {
    functionalLibraryCount,
    setFunctionalLibraryCount,
    sampleLibraryLoaded,
    setSampleLibraryLoaded,
    functionalHistoryCount,
    setFunctionalHistoryCount,
    prometheusUrl,
    setPrometheusUrl,
    observabilitySkipped,
    setObservabilitySkipped,
    hasRunSelection,
    observabilitySkipStorageKey,
    hasTestLibrary,
    hasHistory,
    projectMode,
    isSampleOnlyProject,
    isRealProjectMode,
    ownDataProjectPendingLibrary,
    ownDataModeActive,
    canConfigureRealObservability,
    sampleHistoryReady,
    signalsStepComplete,
    canShowStartOptions,
    currentWizardStep,
    shouldPromoteObservabilityAction,
    shouldPromoteHistoryAction,
    checklist,
    nextRecommendedStep,
  } = useOnboardingStatus({
    organizationId: authData?.organizationId,
    activeProjectId: activeProject?.id,
    hasProject,
    startPath,
    startChoiceCommitted,
    showStartOptionPicker,
  });

  useEffect(() => {
    syncStartPathFromStorage({
      functionalLibraryCount,
      sampleLibraryLoaded,
    });
  }, [functionalLibraryCount, sampleLibraryLoaded, syncStartPathFromStorage]);
  const compactActionButtonSx = {
    borderRadius: 2.25,
    px: 2.25,
    py: 0.85,
    fontSize: '0.95rem',
    lineHeight: 1.2,
    minWidth: 'fit-content',
  };

  const handleBackToStartOptions = () => {
    clearStoredStartPath();
    setSelectedStartOption('');
    setStartChoiceCommitted(false);
    setStartChoiceError(null);
    setShowStartOptionPicker(true);
    requestAnimationFrame(() => {
      document.getElementById('start-options')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const scrollToStep = (stepId: string) => {
    requestAnimationFrame(() => {
      document.getElementById(stepId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleBackToProjectSetup = () => {
    setShowStartOptionPicker(false);
    setStartChoiceCommitted(true);
    scrollToStep('project-step');
  };

  const handleCreateProject = async () => {
    if (!projectName.trim()) {
      setProjectError('Project name is required.');
      return;
    }

    setProjectLoading(true);
    setProjectError(null);
    setProjectSuccess(null);
    setSampleSetupError(null);
    if (pendingStartPathStorageKey) {
      localStorage.setItem(pendingStartPathStorageKey, startPath);
    }

    try {
      const hadAnyProjectBeforeCreate = availableProjects.length > 0;
      const createdProject = await createProject(projectName.trim());
      await refreshProjects();
      await refreshSubscription();
      setCurrentProject(createdProject);
      if (authData?.organizationId) {
        localStorage.setItem(
          `onboarding_start_path_${authData.organizationId}_${createdProject.id}`,
          startPath
        );
      }
      if (pendingStartPathStorageKey) {
        localStorage.removeItem(pendingStartPathStorageKey);
      }
      let successMessage = `Project "${createdProject.name}" is ready.`;

      if (!hadAnyProjectBeforeCreate && startPath === 'sample') {
        setSamplePreloadPending(true);
        try {
          const sampleResult = await api.post<SampleLoadResponse>(
            `/functional/library/load-sample?project_id=${createdProject.id}&source=onboarding_auto&bootstrap_history=true`
          );
          const updatedList = await api.get<FunctionalLibraryListResponse>(
            `/functional/library?project_id=${createdProject.id}`
          );
          setFunctionalLibraryCount(updatedList.total || updatedList.data?.length || 0);
          setSampleLibraryLoaded(true);
          setFunctionalHistoryCount(sampleResult.history_results_inserted || 0);
          successMessage = `Project "${createdProject.name}" is ready. We preloaded a sample test library so you can try your first selection immediately.`;
          setLibrarySuccess(
            sampleResult.history_error
              ? `This is sample data for first exploration. Added ${sampleResult.added} sample tests; sample history will be generated on retry.`
              : `This is sample data for first exploration. Replace it with your real test library to get production-relevant results. Added ${sampleResult.added} sample tests and generated ${sampleResult.history_results_inserted || 0} sample execution history records.`
          );
          if (sampleResult.history_error) {
            setSampleSetupError('Sample library loaded successfully, but execution history is not ready yet. You can retry from onboarding.');
          }
        } catch {
          setSampleSetupError(
            'Sample test library could not be loaded automatically.'
          );
        } finally {
          setSamplePreloadPending(false);
        }
      } else if (!hadAnyProjectBeforeCreate && startPath === 'real') {
        successMessage = `Project "${createdProject.name}" is ready. Upload your own functional test library to start building a real project.`;
      }

      setShowStartOptionPicker(false);
      setProjectSuccess(successMessage);
      setProjectName('');
    } catch (error) {
      console.error('[ONBOARDING] Failed to create project:', error);
      setProjectError(
        normalizeUserFacingError(error, 'Failed to create project. Please try again.')
      );
    } finally {
      setProjectLoading(false);
    }
  };

  const handleGenerateSampleHistory = async () => {
    if (!activeProject?.id) {
      setHistoryError('Create your first project before generating sample execution history.');
      return;
    }

    setHistoryLoading(true);
    setHistoryError(null);
    setHistorySuccess(null);

    try {
      const result = await generateSampleFunctionalHistory(activeProject.id);
      const historyMessage = buildSampleHistorySuccessMessage(
        result as HistoryGenerationResponse,
        functionalHistoryCount
      );
      setFunctionalHistoryCount(historyMessage.count);
      setHistorySuccess(historyMessage.message);
    } catch (error) {
      console.error('[ONBOARDING] Sample execution history generation failed:', error);
      setHistoryError('Sample execution history could not be generated. Please try again.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleUploadFunctionalHistory = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file || !activeProject?.id) {
      return;
    }

    setHistoryLoading(true);
    setHistoryError(null);
    setHistorySuccess(null);

    const reader = new FileReader();
    reader.onload = async (loadEvent) => {
      try {
        const payload = JSON.parse(loadEvent.target?.result as string);
        if (!Array.isArray(payload)) {
          throw new Error('Functional execution history JSON must be an array of records.');
        }

        const result = await importFunctionalHistory(activeProject.id, payload);
        const summary = await fetchFunctionalHistorySummary(activeProject.id);
        setFunctionalHistoryCount(summary.records_imported || result.records_imported || 0);
        setHistorySuccess(
          `Functional execution history uploaded. Imported ${result.records_imported} records.`
        );
      } catch (error) {
        console.error('[ONBOARDING] Functional execution history upload failed:', error);
        setHistoryError(
          'Functional execution history could not be uploaded. Check the format guide and try again.'
        );
      } finally {
        setHistoryLoading(false);
        event.target.value = '';
      }
    };

    reader.readAsText(file);
  };

  const handleLoadSampleLibrary = async () => {
    if (!activeProject?.id) {
      setUploadError('Create your first project before adding a library.');
      return;
    }

    setLibraryLoading(true);
    setUploadError(null);
    setLibrarySuccess(null);
    setSampleSetupError(null);

    try {
      const result = await api.post<SampleLoadResponse>(
        `/functional/library/load-sample?project_id=${activeProject.id}&source=manual&bootstrap_history=true`
      );
      const updatedList = await api.get<FunctionalLibraryListResponse>(
        `/functional/library?project_id=${activeProject.id}`
      );
      setFunctionalLibraryCount(updatedList.total || updatedList.data?.length || 0);
      setSampleLibraryLoaded(true);
      setFunctionalHistoryCount(result.history_results_inserted || functionalHistoryCount);
      setLibrarySuccess(
        result.history_error
          ? `Sample test library ready. History generation was deferred and can be retried.`
          : `Sample test library ready. Added ${result.added} tests and generated ${result.history_results_inserted || 0} sample execution history records.`
      );
      if (result.history_error) {
        setSampleSetupError('The library is ready, but sample execution history could not be generated yet.');
      }
    } catch (error) {
      setUploadError(formatApiError(error, 'Failed to load the sample test library.'));
    } finally {
      setLibraryLoading(false);
    }
  };

  const handleSaveObservability = async () => {
    setObservabilityLoading(true);
    setObservabilityError(null);
    setObservabilitySuccess(null);

    try {
      await api.put('/organization-config/observability', {
        prometheus_url: prometheusUrl.trim(),
        prometheus_lookback: '5m',
      });
      if (observabilitySkipStorageKey) {
        localStorage.removeItem(observabilitySkipStorageKey);
      }
      setObservabilitySkipped(false);
      setObservabilitySuccess('Observability connected successfully.');
    } catch (error) {
      setObservabilityError(
        formatApiError(error, 'Failed to save observability configuration.')
      );
    } finally {
      setObservabilityLoading(false);
    }
  };

  const handleSkipObservability = () => {
    if (observabilitySkipStorageKey) {
      localStorage.setItem(observabilitySkipStorageKey, 'true');
    }
    setObservabilitySkipped(true);
    setObservabilityError(null);
    setObservabilitySuccess('You can connect observability later from Settings.');
  };

  const handleUploadLibrary = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file || !activeProject?.id) {
      return;
    }

    setLibraryLoading(true);
    setUploadError(null);
    setLibrarySuccess(null);

    const reader = new FileReader();
    reader.onload = async (loadEvent) => {
      try {
        const payload = JSON.parse(loadEvent.target?.result as string);
        if (!Array.isArray(payload)) {
          throw new Error('Functional library JSON must be an array of tests.');
        }

        const result = await api.post<UploadResult>(
          `/functional/library/upload-library?project_id=${activeProject.id}`,
          payload
        );
        const updatedList = await api.get<FunctionalLibraryListResponse>(
          `/functional/library?project_id=${activeProject.id}`
        );
        setFunctionalLibraryCount(updatedList.total || updatedList.data?.length || 0);
        setSampleLibraryLoaded(false);
        setSampleSetupError(null);
        setLibrarySuccess(
          `Library uploaded. Added ${result.added || 0}, updated ${result.updated || 0}, skipped ${result.skipped || 0}.`
        );
      } catch (error) {
        setUploadError(formatApiError(error, 'Failed to upload the functional library.'));
      } finally {
        setLibraryLoading(false);
        event.target.value = '';
      }
    };

    reader.readAsText(file);
  };

  const renderChoiceBullet = (text: string) => (
    <Stack direction="row" spacing={1.1} alignItems="flex-start">
      <CheckCircleOutlineIcon sx={{ color: '#39FF14', fontSize: 18, mt: '2px' }} />
      <Typography variant="body2" color="text.secondary">
        {text}
      </Typography>
    </Stack>
  );

  const shouldShowProjectStep = !canShowStartOptions && (!hasProject || currentWizardStep !== 'start');
  const shouldShowLibraryStep = hasProject || currentWizardStep === 'library' || currentWizardStep === 'signals' || currentWizardStep === 'selection' || currentWizardStep === 'done';
  const shouldShowSignalsStep = hasTestLibrary || currentWizardStep === 'signals' || currentWizardStep === 'selection' || currentWizardStep === 'done';
  const shouldShowSelectionStep = signalsStepComplete || currentWizardStep === 'selection' || currentWizardStep === 'done';
  const sampleLibraryPreparing =
    projectMode === 'sample' &&
    !hasTestLibrary &&
    !sampleSetupError &&
    (projectLoading || libraryLoading || samplePreloadPending);
  const showLibraryUploadControls = !sampleLibraryPreparing;

  return (
    <Box className="page-transition" sx={{ width: '100%', maxWidth: 1120, mx: 'auto' }}>
      <PageHeader
        icon={<ScienceIcon fontSize="inherit" />}
        title="Welcome to Quantik Mind"
        subtitle="Set up your Free workspace and prepare your first functional selection."
      />

      <Paper
        elevation={3}
        sx={{
          borderRadius: 3,
          p: { xs: 2, md: 3 },
          background: 'linear-gradient(180deg, rgba(16, 20, 27, 0.98), rgba(10, 14, 20, 0.98))',
        }}
      >
        <Stack spacing={3}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Free Onboarding
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Your organization is ready. Complete the next recommended setup steps and launch your first functional selection when you are ready.
              </Typography>
            </Box>
            <Chip
              label={`Plan: ${subscription?.plan_label?.toUpperCase() || 'FREE'}`}
              sx={{
                color: '#DFFFE6',
                bgcolor: 'rgba(57, 255, 20, 0.12)',
                border: '1px solid rgba(57, 255, 20, 0.18)',
              }}
            />
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
            {checklist.map((step) => (
              <Chip
                key={step.key}
                icon={step.complete ? <CheckCircleIcon /> : <LockOpenIcon />}
                label={`${step.label}${step.optional ? ' (Optional)' : ''}${
                  step.key === nextRecommendedStep ? ' · Recommended next' : ''
                }`}
                sx={{
                  color: step.complete ? '#DFFFE6' : '#C9D1D9',
                  bgcolor: step.complete ? 'rgba(57, 255, 20, 0.1)' : 'rgba(255,255,255,0.05)',
                  border: step.complete
                    ? '1px solid rgba(57, 255, 20, 0.18)'
                    : '1px solid rgba(255,255,255,0.08)',
                  boxShadow:
                    step.key === nextRecommendedStep
                      ? '0 0 0 1px rgba(57,255,20,0.18)'
                      : 'none',
                }}
              />
            ))}
          </Stack>

          <StepCard
            title="Account Created"
            description="Your Free workspace is active and ready for project setup."
            complete={accountReady}
            active={false}
          >
            <Typography variant="body2" color="text.secondary">
              {authData?.organizationName || 'Your organization'} is provisioned on the Free plan with functional selection enabled.
            </Typography>
          </StepCard>

          {canShowStartOptions && (
            <Card
              id="start-options"
              className="enterprise-card"
              sx={{
                borderRadius: 3,
                borderColor: 'rgba(57,255,20,0.28)',
                boxShadow: '0 0 0 1px rgba(57,255,20,0.18)',
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Stack spacing={2.25}>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      {hasProject ? 'Change how this project starts' : 'How do you want to start?'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.7 }}>
                      Start instantly with sample data, or use your own data if your library is ready.
                    </Typography>
                  </Box>

                  <RadioGroup
                    value={selectedStartOption}
                    onChange={(event) => selectStartPath(event.target.value as StartPath)}
                  >
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <Card
                      sx={{
                        flex: 1,
                        display: 'flex',
                        minHeight: '100%',
                        borderRadius: 3,
                        border: selectedStartOption === 'sample'
                          ? '1px solid rgba(57,255,20,0.28)'
                          : '1px solid rgba(255,255,255,0.08)',
                        bgcolor: selectedStartOption === 'sample'
                          ? 'rgba(57,255,20,0.05)'
                          : 'rgba(255,255,255,0.02)',
                      }}
                    >
                      <CardContent sx={{ display: 'flex', flex: 1 }}>
                        <Stack spacing={1.5} sx={{ flex: 1 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1.5}>
                            <Box>
                              <Typography variant="overline" sx={{ color: '#39FF14', letterSpacing: '0.08em', fontWeight: 700 }}>
                                Recommended for your first run
                              </Typography>
                              <Typography variant="subtitle1" fontWeight={700}>
                                Explore with sample data
                              </Typography>
                            </Box>
                            <FormControlLabel
                              value="sample"
                              control={<Radio sx={{ color: 'rgba(255,255,255,0.54)' }} />}
                              label=""
                              sx={{ m: 0, flexShrink: 0 }}
                            />
                          </Stack>
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                            Use a preloaded test library and sample execution history to see Quantik Mind in action immediately.
                          </Typography>
                          <Stack spacing={0.75}>
                            {renderChoiceBullet('Preloaded functional test library')}
                            {renderChoiceBullet('Sample execution history')}
                            {renderChoiceBullet('Instant first selection')}
                            {renderChoiceBullet('No Prometheus setup required')}
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>

                    <Card
                      sx={{
                        flex: 1,
                        display: 'flex',
                        minHeight: '100%',
                        borderRadius: 3,
                        border: selectedStartOption === 'real'
                          ? '1px solid rgba(57,255,20,0.28)'
                          : '1px solid rgba(255,255,255,0.08)',
                        bgcolor: selectedStartOption === 'real'
                          ? 'rgba(57,255,20,0.05)'
                          : 'rgba(255,255,255,0.02)',
                      }}
                    >
                      <CardContent sx={{ display: 'flex', flex: 1 }}>
                        <Stack spacing={1.5} sx={{ flex: 1 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1.5}>
                            <Typography variant="subtitle1" fontWeight={700}>
                              Use your own data
                            </Typography>
                            <FormControlLabel
                              value="real"
                              control={<Radio sx={{ color: 'rgba(255,255,255,0.54)' }} />}
                              label=""
                              sx={{ m: 0, flexShrink: 0 }}
                            />
                          </Stack>
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                            Upload your own functional test library and connect real signals when you are ready.
                          </Typography>
                          <Stack spacing={0.75}>
                            {renderChoiceBullet('Bring your own test library')}
                            {renderChoiceBullet('Upload historical execution data')}
                            {renderChoiceBullet('Connect observability / Prometheus')}
                            {renderChoiceBullet('Prepare real CI/CD adoption')}
                          </Stack>
                          <Stack spacing={1}>
                            <ResourceLink
                              href={TEMPLATE_PATHS.functionalLibrary}
                              target="_blank"
                              sx={{ alignSelf: 'flex-start' }}
                            >
                              Download functional test library template
                            </ResourceLink>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Stack>
                  </RadioGroup>
                  {startChoiceError && (
                    <Alert severity="warning" sx={{ borderRadius: 2.5 }}>
                      {startChoiceError}
                    </Alert>
                  )}

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
                    <Button
                      variant="contained"
                      onClick={commitStartPath}
                      sx={{ ...compactActionButtonSx, ...primaryActionButtonSx }}
                    >
                      Next
                    </Button>
                    <ResourceLink
                      href={DOC_PATHS.dataUploadFormats}
                      target="_blank"
                    >
                      View upload format guide
                    </ResourceLink>
                    <ResourceLink
                      href={DOC_PATHS.observabilitySetup}
                      target="_blank"
                    >
                      View observability setup guide
                    </ResourceLink>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          )}

          {shouldShowProjectStep && (
          <StepCard
            stepId="project-step"
            title="Create First Project"
            description={
              startPath === 'sample'
                ? 'Create your first project and let Quantik Mind preload a guided sample setup.'
                : 'Create your first project, then upload your own functional test library.'
            }
            complete={hasProject}
            active={currentWizardStep === 'project'}
          >
            {hasProject ? (
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  Active project
                </Typography>
                <Typography variant="subtitle1" fontWeight={700}>
                  {activeProject?.name}
                </Typography>
                {!hasTestLibrary && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                    <Chip
                      label={projectMode === 'sample' ? 'Sample project' : 'Own-data project'}
                      size="small"
                      sx={{
                        alignSelf: 'flex-start',
                        color: projectMode === 'sample' ? '#DFFFE6' : '#E6EDF3',
                        bgcolor:
                          projectMode === 'sample'
                            ? 'rgba(57,255,20,0.1)'
                            : 'rgba(255,255,255,0.06)',
                        border:
                          projectMode === 'sample'
                            ? '1px solid rgba(57,255,20,0.18)'
                            : '1px solid rgba(255,255,255,0.08)',
                      }}
                    />
                    <Button
                      variant="text"
                      onClick={() => setShowStartOptionPicker(true)}
                      sx={{ ...secondaryActionButtonSx, px: 0, alignSelf: 'flex-start' }}
                    >
                      Change start option
                    </Button>
                  </Stack>
                )}
              </Stack>
            ) : (
              <Stack spacing={2}>
                <TextField
                  label="Project Name"
                  value={projectName}
                  onChange={(event) => setProjectName(event.target.value)}
                  placeholder="e.g. Banking App Production"
                  disabled={projectLoading}
                  fullWidth
                />
                {projectError && <Alert severity="error">{projectError}</Alert>}
                {projectSuccess && <Alert severity="success">{projectSuccess}</Alert>}
                <Button
                  variant="contained"
                  onClick={() => void handleCreateProject()}
                  disabled={projectLoading}
                  startIcon={projectLoading ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                  sx={{
                    alignSelf: 'flex-start',
                    ...compactActionButtonSx,
                    ...primaryActionButtonSx,
                  }}
                >
                  {projectLoading
                    ? startPath === 'sample'
                      ? 'Setting up sample project...'
                      : 'Creating project...'
                    : startPath === 'sample'
                      ? 'Start with sample project'
                      : 'Start with my data'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleBackToStartOptions}
                  sx={{ ...compactActionButtonSx, ...outlineActionButtonSx }}
                >
                  Back to start options
                </Button>
              </Stack>
            )}
          </StepCard>
          )}

          {shouldShowLibraryStep && (
          <StepCard
            stepId="library-step"
            title="Add Functional Library"
            description="Upload your own functional tests or follow the recommended sample setup path."
            complete={hasTestLibrary}
            active={currentWizardStep === 'library'}
          >
            <Stack spacing={2}>
              {sampleSetupError && (
                <Alert
                  severity="warning"
                  sx={{
                    borderRadius: 2.5,
                    bgcolor: 'rgba(255, 190, 92, 0.08)',
                    color: '#F5E9C8',
                    border: '1px solid rgba(255, 190, 92, 0.16)',
                  }}
                >
                  Sample test library could not be loaded automatically.
                </Alert>
              )}
              {sampleLibraryLoaded && (
                <Alert
                  severity="info"
                  sx={{
                    borderRadius: 2.5,
                    bgcolor: 'rgba(57, 255, 20, 0.08)',
                    color: '#EAF8ED',
                    border: '1px solid rgba(57, 255, 20, 0.16)',
                  }}
                >
                  We preloaded a sample test library so you can try your first selection immediately.
                  Run your first selection when you are ready. Replace it with your real test library later for production-relevant results.
                </Alert>
              )}
              {sampleLibraryPreparing && (
                <Alert
                  severity="info"
                  sx={{
                    borderRadius: 2.5,
                    bgcolor: 'rgba(57, 255, 20, 0.08)',
                    color: '#EAF8ED',
                    border: '1px solid rgba(57, 255, 20, 0.16)',
                  }}
                >
                  Preparing your sample test library...
                </Alert>
              )}
              {isRealProjectMode && (
                <Alert
                  severity="info"
                  sx={{
                    borderRadius: 2.5,
                    bgcolor: 'rgba(255,255,255,0.04)',
                    color: 'rgba(255,255,255,0.82)',
                  }}
                >
                  Your own library is active. Next, upload historical execution data or connect observability to improve selection quality with at least one real signal source.
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary">
                {hasTestLibrary
                  ? sampleLibraryLoaded
                    ? `Sample test library loaded for ${activeProject?.name}. Replace it with your real test library for production results.`
                    : `${functionalLibraryCount} functional tests are ready in ${activeProject?.name}.`
                  : sampleLibraryPreparing
                    ? 'We are loading the guided sample library and execution history for this project.'
                  : 'Add test coverage data before running your first selection.'}
              </Typography>
              {hasProject && ownDataModeActive && !hasTestLibrary && !sampleLibraryPreparing && (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.62)' }}>
                  Upload is available now. Upload your functional test library to continue.
                </Typography>
              )}
              {sampleLibraryLoaded && (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.62)' }}>
                  Sample test library is active. Replace it only when you are ready to switch this project to your own data.
                </Typography>
              )}
              {!hasTestLibrary && ownDataModeActive && !sampleLibraryPreparing && (
                <Alert
                  severity="info"
                  sx={{
                    borderRadius: 2.5,
                    bgcolor: 'rgba(255,255,255,0.04)',
                    color: 'rgba(255,255,255,0.82)',
                  }}
                >
                  Start by uploading your own functional test library. Historical data import and observability setup become useful after the library exists.
                </Alert>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <ResourceLink
                  href={TEMPLATE_PATHS.functionalLibrary}
                  target="_blank"
                >
                  Download functional test library template
                </ResourceLink>
                <ResourceLink
                  href={DOC_PATHS.dataUploadFormats}
                  target="_blank"
                >
                  View upload format guide
                </ResourceLink>
              </Stack>

              {!hasProject && (
                <Alert severity="info">
                  Create your first project before adding a functional library.
                </Alert>
              )}

              {uploadError && <Alert severity="error">{uploadError}</Alert>}
              {librarySuccess && <Alert severity="success">{librarySuccess}</Alert>}

              {showLibraryUploadControls && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <Button
                    variant={currentWizardStep === 'library' && ownDataModeActive ? 'contained' : 'outlined'}
                    component="label"
                    startIcon={<UploadFileIcon />}
                    disabled={!hasProject || libraryLoading}
                    sx={{
                      ...compactActionButtonSx,
                      ...(currentWizardStep === 'library' && ownDataModeActive
                        ? primaryActionButtonSx
                        : outlineActionButtonSx),
                    }}
                  >
                    {sampleLibraryLoaded ? 'Replace sample library' : 'Upload your real test library'}
                    <input
                      hidden
                      type="file"
                      accept=".json"
                      onChange={(event) => void handleUploadLibrary(event)}
                    />
                  </Button>
                  {projectMode === 'sample' && !sampleLibraryLoaded && !hasTestLibrary && (
                    <Button
                      variant={currentWizardStep === 'library' ? 'contained' : 'outlined'}
                      onClick={() => void handleLoadSampleLibrary()}
                      disabled={!hasProject || libraryLoading}
                      sx={{
                        ...compactActionButtonSx,
                        ...(currentWizardStep === 'library'
                          ? primaryActionButtonSx
                          : outlineActionButtonSx),
                      }}
                    >
                      {libraryLoading
                        ? 'Preparing sample test library...'
                        : sampleSetupError
                          ? 'Retry sample setup'
                          : 'Load sample test library'}
                    </Button>
                  )}
                  {!hasTestLibrary && (
                    <Button
                      variant="outlined"
                      onClick={hasProject ? handleBackToProjectSetup : handleBackToStartOptions}
                      sx={{ ...compactActionButtonSx, ...outlineActionButtonSx }}
                    >
                      {hasProject ? 'Back to project setup' : 'Back to start options'}
                    </Button>
                  )}
                </Stack>
              )}
            </Stack>
          </StepCard>
          )}

          {shouldShowSignalsStep && (
          <StepCard
            stepId="signals-step"
            title="Add Execution History & Signals"
            description="Execution history comes first. Observability is optional and additive."
            complete={signalsStepComplete}
            active={currentWizardStep === 'signals'}
          >
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75 }}>
                  Functional execution history
                </Typography>
                {projectMode === 'sample' ? (
                  !hasTestLibrary ? (
                    <Alert
                      severity="info"
                      sx={{
                        borderRadius: 2.5,
                        bgcolor: 'rgba(255,255,255,0.04)',
                        color: 'rgba(255,255,255,0.82)',
                        mb: 1.25,
                      }}
                    >
                      Sample execution history becomes available after the sample test library finishes loading for this project.
                    </Alert>
                  ) : sampleHistoryReady ? (
                    <Alert
                      severity="success"
                      sx={{
                        borderRadius: 2.5,
                        bgcolor: 'rgba(57,255,20,0.08)',
                        color: '#EAF8ED',
                        border: '1px solid rgba(57,255,20,0.16)',
                        mb: 1.25,
                      }}
                    >
                      Sample execution history ready. {functionalHistoryCount} historical result records are available for the sample project.
                    </Alert>
                  ) : (
                    <Alert
                      severity="warning"
                      sx={{
                        borderRadius: 2.5,
                        bgcolor: 'rgba(255, 190, 92, 0.08)',
                        color: '#F5E9C8',
                        border: '1px solid rgba(255, 190, 92, 0.16)',
                        mb: 1.25,
                      }}
                    >
                      Sample execution history is missing. Generate it to complete the guided sample setup.
                    </Alert>
                  )
                ) : ownDataProjectPendingLibrary ? (
                  <Alert
                    severity="info"
                    sx={{
                      borderRadius: 2.5,
                      bgcolor: 'rgba(255,255,255,0.04)',
                      color: 'rgba(255,255,255,0.82)',
                      mb: 1.25,
                    }}
                  >
                    Upload your functional test library first. Historical execution data can be imported after the project library exists because history rows must match library test IDs.
                  </Alert>
                ) : (
                  <Alert
                    severity={hasHistory ? 'success' : 'info'}
                    sx={{
                      borderRadius: 2.5,
                      bgcolor: hasHistory ? 'rgba(57,255,20,0.08)' : 'rgba(255,255,255,0.04)',
                      color: hasHistory ? '#EAF8ED' : 'rgba(255,255,255,0.82)',
                      border: hasHistory ? '1px solid rgba(57,255,20,0.16)' : undefined,
                      mb: 1.25,
                    }}
                  >
                    {hasHistory
                      ? `Real execution history is connected. ${functionalHistoryCount} records are available for this project.`
                      : 'No real execution history yet. Free onboarding stays focused on functional execution history here. You can import history now, then optionally add observability for stronger signal quality.'}
                  </Alert>
                )}

                {historyError && <Alert severity="error" sx={{ mb: 1.25 }}>{historyError}</Alert>}
                {historySuccess && <Alert severity="success" sx={{ mb: 1.25 }}>{historySuccess}</Alert>}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                  {projectMode === 'sample' ? (
                    <Button
                      variant={currentWizardStep === 'signals' ? 'contained' : 'outlined'}
                      onClick={() => void handleGenerateSampleHistory()}
                      disabled={!hasProject || !hasTestLibrary || historyLoading || sampleHistoryReady}
                      sx={{
                        ...compactActionButtonSx,
                        ...(currentWizardStep === 'signals' ? primaryActionButtonSx : outlineActionButtonSx),
                      }}
                    >
                      {historyLoading ? 'Generating sample history...' : 'Generate sample execution history'}
                    </Button>
                  ) : (
                    <Stack spacing={0.75} sx={{ alignItems: 'flex-start' }}>
                      <Button
                        variant={shouldPromoteHistoryAction ? 'contained' : 'outlined'}
                        component="label"
                        disabled={!hasProject || !hasTestLibrary}
                        sx={{
                          ...compactActionButtonSx,
                          ...(shouldPromoteHistoryAction ? primaryActionButtonSx : outlineActionButtonSx),
                        }}
                      >
                        Upload historical execution data
                        <input
                          hidden
                          type="file"
                          accept=".json"
                          onChange={(event) => void handleUploadFunctionalHistory(event)}
                        />
                      </Button>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.62)' }}>
                        History rows must reference `test_id` values from your uploaded functional test library. Service names should stay consistent across library and history data.
                      </Typography>
                    </Stack>
                  )}
                  <ResourceLink
                    href={DOC_PATHS.dataUploadFormats}
                    target="_blank"
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    View upload format guide
                  </ResourceLink>
                </Stack>
              </Box>

              {isSampleOnlyProject ? (
                <Alert
                  severity="info"
                  sx={{
                    borderRadius: 2.5,
                    bgcolor: 'rgba(255,255,255,0.04)',
                    color: 'rgba(255,255,255,0.82)',
                  }}
                >
                  Sample projects use demo tests and sample execution history. Prometheus is optional here and becomes more useful after you switch to your own library.
                </Alert>
              ) : !isRealProjectMode ? (
                <Alert
                  severity="info"
                  sx={{
                    borderRadius: 2.5,
                    bgcolor: 'rgba(255,255,255,0.04)',
                    color: 'rgba(255,255,255,0.82)',
                  }}
                >
                  Upload your own functional test library first. Prometheus becomes relevant once this project is using real library data.
                </Alert>
              ) : null}
              <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
              <Typography variant="subtitle2" fontWeight={700}>
                Optional observability
              </Typography>
              {ownDataModeActive && (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.62)' }}>
                  After importing history, or after saving or skipping observability, you can continue directly to Run your first selection.
                </Typography>
              )}
              <TextField
                label="Prometheus URL"
                value={prometheusUrl}
                onChange={(event) => setPrometheusUrl(event.target.value)}
                placeholder="e.g. http://prometheus:9090"
                disabled={observabilityLoading || !canConfigureRealObservability}
                fullWidth
              />
              <FormHelperText sx={{ color: 'text.secondary', m: 0 }}>
                {isSampleOnlyProject
                  ? 'Available for real projects only. Upload your own test library first, then connect Prometheus when you want runtime-aware selection.'
                  : !isRealProjectMode
                    ? 'Available after your own functional test library is active.'
                  : 'Leave this empty if you want to complete onboarding first and connect Prometheus later.'}
              </FormHelperText>

              {observabilityError && <Alert severity="error">{observabilityError}</Alert>}
              {observabilitySuccess && <Alert severity="success">{observabilitySuccess}</Alert>}

              <ResourceLink
                href={DOC_PATHS.observabilitySetup}
                target="_blank"
                sx={{ alignSelf: 'flex-start' }}
              >
                View observability setup guide
              </ResourceLink>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  variant={shouldPromoteObservabilityAction ? 'contained' : 'outlined'}
                  onClick={() => void handleSaveObservability()}
                  disabled={observabilityLoading || !prometheusUrl.trim() || !canConfigureRealObservability}
                  startIcon={observabilityLoading ? <CircularProgress size={16} /> : <MonitorHeartIcon />}
                  sx={{
                    ...compactActionButtonSx,
                    ...(shouldPromoteObservabilityAction
                      ? primaryActionButtonSx
                      : outlineActionButtonSx),
                  }}
                >
                  {observabilityLoading ? 'Saving...' : 'Save configuration'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleSkipObservability}
                  disabled={observabilityLoading || !canConfigureRealObservability}
                  sx={{ ...compactActionButtonSx, ...outlineActionButtonSx }}
                >
                  Skip for now
                </Button>
                {currentWizardStep === 'signals' && (
                  <Button
                    variant="outlined"
                    onClick={() => scrollToStep('library-step')}
                    sx={{ ...compactActionButtonSx, ...outlineActionButtonSx }}
                  >
                    Back to library
                  </Button>
                )}
              </Stack>
            </Stack>
          </StepCard>
          )}

          {shouldShowSelectionStep && (
          <StepCard
            stepId="selection-step"
            title="Run your first selection"
            description="When your project and functional library are ready, continue into the existing selection flow."
            complete={hasRunSelection}
            active={currentWizardStep === 'selection'}
          >
            <Stack spacing={1.75} alignItems="flex-start">
              {sampleLibraryLoaded && !hasRunSelection && (
                <Alert
                  severity="info"
                  sx={{
                    borderRadius: 2.5,
                    bgcolor: 'rgba(255,255,255,0.04)',
                    color: 'rgba(255,255,255,0.82)',
                  }}
                >
                  Sample setup is ready. Run one selection now, then upload your real test library for production-relevant results.
                </Alert>
              )}
              {sampleLibraryLoaded && hasRunSelection && (
                <Alert
                  severity="info"
                  sx={{
                    borderRadius: 2.5,
                    bgcolor: 'rgba(255,255,255,0.04)',
                    color: 'rgba(255,255,255,0.82)',
                  }}
                >
                  Your first result used sample data. Upload your real test library to get meaningful recommendations for your system.
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 680 }}>
                Quantik Mind will use your uploaded library, or the preloaded sample test library, to produce the first functional selection. You can review the results in Metrics & Results afterward.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems="center">
                <Button
                  variant={currentWizardStep === 'selection' ? 'contained' : 'outlined'}
                  onClick={() => {
                    if (!hasProject) {
                      setProjectError('Create your first project before running a selection.');
                      return;
                    }
                    if (!hasTestLibrary) {
                      setUploadError('Add a functional test library before running your first selection.');
                      return;
                    }
                    navigate('/quantum');
                  }}
                  disabled={!accountReady || !hasProject || !hasTestLibrary}
                  startIcon={<ScienceIcon />}
                  sx={{
                    ...compactActionButtonSx,
                    ...(currentWizardStep === 'selection'
                      ? primaryActionButtonSx
                      : outlineActionButtonSx),
                  }}
                >
                  Run first selection
                </Button>
                <ResourceLink
                  href={DOC_PATHS.apiQuickstart}
                  target="_blank"
                  onClick={() => {
                    void trackFrontendEvent('ci_integration_started', {
                      source: 'onboarding',
                      project_id: activeProject?.id,
                    }).catch(() => undefined);
                  }}
                >
                  View API quickstart
                </ResourceLink>
                {currentWizardStep === 'selection' && (
                  <Button
                    variant="outlined"
                    onClick={() =>
                      projectMode === 'sample' && signalsStepComplete
                        ? scrollToStep('library-step')
                        : scrollToStep('signals-step')
                    }
                    sx={{ ...compactActionButtonSx, ...outlineActionButtonSx }}
                  >
                    {projectMode === 'sample' && signalsStepComplete
                      ? 'Back to sample setup'
                      : 'Back to execution history & signals'}
                  </Button>
                )}
              </Stack>
            </Stack>
          </StepCard>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
